import React, { useState, useEffect } from 'react'
import { FileText, Plus, Trash2, Edit3, ExternalLink, Save, X, Loader2, Link } from 'lucide-react'
import { Button, Input } from '@components/common'
import { organizationDocumentService } from '@services/organizationDocumentService'
import toast from 'react-hot-toast'
import './OrganizationDocuments.scss'

const EMPTY_FORM = { title: '', drive_link: '' }

const OrganizationDocuments = () => {
	const [documents, setDocuments] = useState([])
	const [loading, setLoading] = useState(true)
	const [showForm, setShowForm] = useState(false)
	const [editingId, setEditingId] = useState(null)
	const [form, setForm] = useState(EMPTY_FORM)
	const [errors, setErrors] = useState({})
	const [saving, setSaving] = useState(false)
	const [deletingId, setDeletingId] = useState(null)

	useEffect(() => {
		fetchDocuments()
	}, [])

	const fetchDocuments = async () => {
		setLoading(true)
		try {
			const res = await organizationDocumentService.getAll()
			if (res.status === 200) {
				setDocuments(res.data || [])
			}
		} catch {
			toast.error('Failed to load organization documents')
		} finally {
			setLoading(false)
		}
	}

	const validate = () => {
		const errs = {}
		if (!form.title.trim()) errs.title = 'Title is required'
		if (!form.drive_link.trim()) errs.drive_link = 'Google Drive link is required'
		setErrors(errs)
		return Object.keys(errs).length === 0
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		if (!validate()) return
		setSaving(true)
		try {
			if (editingId) {
				const res = await organizationDocumentService.update(editingId, form.title.trim(), form.drive_link.trim())
				if (res.status === 200) {
					setDocuments((prev) => prev.map((d) => (d.id === editingId ? res.data : d)))
					toast.success('Document updated')
					resetForm()
				}
			} else {
				const res = await organizationDocumentService.create(form.title.trim(), form.drive_link.trim())
				if (res.status === 201) {
					setDocuments((prev) => [res.data, ...prev])
					toast.success('Document added')
					resetForm()
				}
			}
		} catch {
			toast.error(editingId ? 'Failed to update document' : 'Failed to add document')
		} finally {
			setSaving(false)
		}
	}

	const handleEdit = (doc) => {
		setEditingId(doc.id)
		setForm({ title: doc.title, drive_link: doc.drive_link })
		setErrors({})
		setShowForm(true)
	}

	const handleDelete = async (id) => {
		setDeletingId(id)
		try {
			await organizationDocumentService.delete(id)
			setDocuments((prev) => prev.filter((d) => d.id !== id))
			toast.success('Document removed')
		} catch {
			toast.error('Failed to delete document')
		} finally {
			setDeletingId(null)
		}
	}

	const resetForm = () => {
		setForm(EMPTY_FORM)
		setErrors({})
		setEditingId(null)
		setShowForm(false)
	}

	const handleChange = (field, value) => {
		setForm((prev) => ({ ...prev, [field]: value }))
		if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }))
	}

	return (
		<section className="org-documents">
			<div className="org-documents__header">
				<h3 className="org-documents__title">
					<FileText size={18} />
					Organization Documents
				</h3>
				{!showForm && (
					<Button variant="outline" size="small" onClick={() => setShowForm(true)}>
						<Plus size={14} />
						Add Document
					</Button>
				)}
			</div>

			<p className="org-documents__description">
				Store links to key organization documents hosted on Google Drive.
			</p>

			{/* Add / Edit Form */}
			{showForm && (
				<form className="org-documents__form" onSubmit={handleSubmit}>
					<div className="org-documents__form-fields">
						<Input
							label="Document Title"
							value={form.title}
							onChange={(e) => handleChange('title', e.target.value)}
							error={errors.title}
							placeholder="e.g. Information Security Policy"
							disabled={saving}
						/>
						<Input
							label="Google Drive Link"
							value={form.drive_link}
							onChange={(e) => handleChange('drive_link', e.target.value)}
							error={errors.drive_link}
							placeholder="https://drive.google.com/..."
							disabled={saving}
						/>
					</div>
					<div className="org-documents__form-actions">
						<Button type="button" variant="outline" size="small" onClick={resetForm} disabled={saving}>
							<X size={14} />
							Cancel
						</Button>
						<Button type="submit" variant="primary" size="small" disabled={saving}>
							{saving ? (
								<><Loader2 size={14} className="animate-spin" />{editingId ? 'Saving...' : 'Adding...'}</>
							) : (
								<><Save size={14} />{editingId ? 'Save Changes' : 'Add Document'}</>
							)}
						</Button>
					</div>
				</form>
			)}

			{/* Document List */}
			{loading ? (
				<div className="org-documents__loading">
					<Loader2 size={20} className="animate-spin" />
					<span>Loading documents...</span>
				</div>
			) : documents.length === 0 ? (
				<div className="org-documents__empty">
					<Link size={32} />
					<p>No documents added yet.</p>
				</div>
			) : (
				<ul className="org-documents__list">
					{documents.map((doc) => (
						<li key={doc.id} className="org-documents__item">
							<div className="org-documents__item-info">
								<FileText size={16} />
								<div>
									<span className="org-documents__item-title">{doc.title}</span>
									<a
										href={doc.drive_link}
										target="_blank"
										rel="noopener noreferrer"
										className="org-documents__item-link"
									>
										<ExternalLink size={12} />
										Open in Drive
									</a>
								</div>
							</div>
							<div className="org-documents__item-actions">
								<button
									type="button"
									className="org-documents__icon-btn"
									onClick={() => handleEdit(doc)}
									title="Edit"
								>
									<Edit3 size={15} />
								</button>
								<button
									type="button"
									className="org-documents__icon-btn org-documents__icon-btn--danger"
									onClick={() => handleDelete(doc.id)}
									disabled={deletingId === doc.id}
									title="Delete"
								>
									{deletingId === doc.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
								</button>
							</div>
						</li>
					))}
				</ul>
			)}
		</section>
	)
}

export default OrganizationDocuments
